/**
 * Admin Ticket Dashboard
 * ======================
 * Admin page to view, respond to, and manage support tickets.
 * Endpoints: GET /api/support/admin/tickets, PATCH /api/support/admin/tickets/:id
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, 
  RefreshCw, 
  Search, 
  Filter, 
  Mail, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  MessageSquare,
  Send,
  X,
  ChevronDown,
  User,
  Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  new: { 
    label: 'New', 
    color: '#3B82F6', 
    bgColor: 'rgba(59, 130, 246, 0.15)', 
    icon: AlertCircle 
  },
  open: { 
    label: 'Open', 
    color: '#F59E0B', 
    bgColor: 'rgba(245, 158, 11, 0.15)', 
    icon: Mail 
  },
  'in-progress': { 
    label: 'In Progress', 
    color: '#8B5CF6', 
    bgColor: 'rgba(139, 92, 246, 0.15)', 
    icon: Clock 
  },
  resolved: { 
    label: 'Resolved', 
    color: '#10B981', 
    bgColor: 'rgba(16, 185, 129, 0.15)', 
    icon: CheckCircle 
  },
  closed: { 
    label: 'Closed', 
    color: '#6B7280', 
    bgColor: 'rgba(107, 114, 128, 0.15)', 
    icon: CheckCircle 
  }
};

const AdminTicketDashboard = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchTickets = useCallback(async () => {
    try {
      setError(null);
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      params.append('per_page', '100');
      
      const response = await axios.get(`${API}/support/admin/tickets?${params}`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setError('Failed to load tickets. Please try again.');
    }
  }, [filterStatus]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/support/admin/tickets/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Stats error is non-critical, don't block UI
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTickets(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTickets, fetchStats]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchTickets(), fetchStats()]);
    setRefreshing(false);
  };

  const handleStatusUpdate = async (ticketId, newStatus) => {
    setUpdating(true);
    try {
      await axios.patch(`${API}/support/admin/tickets/${ticketId}`, {
        status: newStatus
      });
      await fetchTickets();
      await fetchStats();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
    setUpdating(false);
  };

  const handleSendResponse = async () => {
    if (!selectedTicket || !responseText.trim()) return;
    
    setUpdating(true);
    try {
      // Preserve current ticket status instead of forcing 'resolved'
      // If the admin wants to resolve it, they can click the status button first
      await axios.patch(`${API}/support/admin/tickets/${selectedTicket.id}`, {
        admin_response: responseText,
        admin_notes: adminNotes || undefined,
        send_response_email: sendEmail,
        status: selectedTicket.status === 'new' ? 'open' : selectedTicket.status
      });
      await fetchTickets();
      await fetchStats();
      setSelectedTicket(null);
      setResponseText('');
      setAdminNotes('');
    } catch (error) {
      console.error('Error sending response:', error);
    }
    setUpdating(false);
  };

  const filteredTickets = tickets.filter(ticket => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ticket.name?.toLowerCase().includes(query) ||
        ticket.email?.toLowerCase().includes(query) ||
        ticket.subject?.toLowerCase().includes(query) ||
        ticket.id?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#1A0F0A',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ color: '#D7B56D', fontSize: '16px' }}>Loading tickets...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#1A0F0A',
      paddingBottom: '100px'
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: 'rgba(26, 15, 10, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '16px',
        borderBottom: '1px solid rgba(165, 124, 51, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(-1)}
          data-testid="back-button"
          style={{
            background: 'none',
            border: 'none',
            color: '#E6D7C3',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <ArrowLeft size={24} />
        </button>
        <h1 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '20px',
          color: '#E6D7C3',
          margin: 0,
          flex: 1
        }}>
          Support Tickets
        </h1>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="refresh-button"
          style={{
            background: 'rgba(215, 181, 109, 0.1)',
            border: '1px solid rgba(215, 181, 109, 0.3)',
            borderRadius: '8px',
            padding: '8px',
            color: '#D7B56D',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      <div style={{ padding: '20px 16px' }}>
        {/* Stats Cards */}
        {stats && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: '12px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ color: '#3B82F6', fontSize: '24px', fontWeight: '700' }}>
                {stats.by_status?.new || 0}
              </div>
              <div style={{ color: '#8A7355', fontSize: '12px', marginTop: '4px' }}>New</div>
            </div>
            <div style={{
              background: 'rgba(245, 158, 11, 0.1)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ color: '#F59E0B', fontSize: '24px', fontWeight: '700' }}>
                {stats.by_status?.open || 0}
              </div>
              <div style={{ color: '#8A7355', fontSize: '12px', marginTop: '4px' }}>Open</div>
            </div>
            <div style={{
              background: 'rgba(139, 92, 246, 0.1)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ color: '#8B5CF6', fontSize: '24px', fontWeight: '700' }}>
                {stats.by_status?.['in-progress'] || 0}
              </div>
              <div style={{ color: '#8A7355', fontSize: '12px', marginTop: '4px' }}>In Progress</div>
            </div>
            <div style={{
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              textAlign: 'center'
            }}>
              <div style={{ color: '#10B981', fontSize: '24px', fontWeight: '700' }}>
                {stats.by_status?.resolved || 0}
              </div>
              <div style={{ color: '#8A7355', fontSize: '12px', marginTop: '4px' }}>Resolved</div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <div style={{
            flex: 1,
            position: 'relative'
          }}>
            <Search 
              size={18} 
              style={{ 
                position: 'absolute', 
                left: '12px', 
                top: '50%', 
                transform: 'translateY(-50%)',
                color: '#8A7355'
              }} 
            />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
              style={{
                width: '100%',
                padding: '12px 12px 12px 40px',
                background: 'rgba(26, 15, 10, 0.8)',
                border: '1px solid rgba(165, 124, 51, 0.2)',
                borderRadius: '10px',
                color: '#E6D7C3',
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            data-testid="filter-button"
            style={{
              background: filterStatus ? 'rgba(215, 181, 109, 0.2)' : 'rgba(26, 15, 10, 0.8)',
              border: '1px solid rgba(165, 124, 51, 0.3)',
              borderRadius: '10px',
              padding: '12px',
              color: filterStatus ? '#D7B56D' : '#8A7355',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Filter size={18} />
            <ChevronDown size={16} />
          </button>
        </div>

        {/* Filter Dropdown */}
        {showFilters && (
          <div style={{
            background: 'rgba(26, 15, 10, 0.95)',
            border: '1px solid rgba(165, 124, 51, 0.3)',
            borderRadius: '12px',
            padding: '12px',
            marginBottom: '20px'
          }}>
            <div style={{ 
              color: '#8A7355', 
              fontSize: '12px', 
              marginBottom: '8px',
              textTransform: 'uppercase'
            }}>
              Filter by Status
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              <button
                onClick={() => { setFilterStatus(''); setShowFilters(false); }}
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  border: 'none',
                  background: !filterStatus ? 'rgba(215, 181, 109, 0.3)' : 'rgba(255,255,255,0.05)',
                  color: !filterStatus ? '#D7B56D' : '#8A7355',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                All
              </button>
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => { setFilterStatus(key); setShowFilters(false); }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: 'none',
                    background: filterStatus === key ? config.bgColor : 'rgba(255,255,255,0.05)',
                    color: filterStatus === key ? config.color : '#8A7355',
                    fontSize: '13px',
                    cursor: 'pointer'
                  }}
                >
                  {config.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tickets List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {error ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#E74C3C'
            }}>
              <AlertCircle size={48} style={{ opacity: 0.7, marginBottom: '16px' }} />
              <div style={{ fontSize: '16px' }}>{error}</div>
              <button
                onClick={handleRefresh}
                style={{
                  marginTop: '16px',
                  padding: '10px 20px',
                  background: 'rgba(215, 181, 109, 0.2)',
                  border: '1px solid rgba(215, 181, 109, 0.3)',
                  borderRadius: '8px',
                  color: '#D7B56D',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Try Again
              </button>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#8A7355'
            }}>
              <MessageSquare size={48} style={{ opacity: 0.5, marginBottom: '16px' }} />
              <div style={{ fontSize: '16px' }}>No tickets found</div>
              <div style={{ fontSize: '13px', marginTop: '8px' }}>
                {filterStatus ? 'Try a different filter' : 'Support tickets will appear here'}
              </div>
            </div>
          ) : (
            filteredTickets.map((ticket) => {
              const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.new;
              const StatusIcon = statusConfig.icon;
              
              return (
                <div
                  key={ticket.id}
                  onClick={() => setSelectedTicket(ticket)}
                  data-testid={`ticket-${ticket.id}`}
                  style={{
                    background: 'rgba(26, 15, 10, 0.8)',
                    border: '1px solid rgba(165, 124, 51, 0.2)',
                    borderRadius: '12px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    marginBottom: '12px'
                  }}>
                    <div>
                      <div style={{ 
                        color: '#E6D7C3', 
                        fontSize: '15px', 
                        fontWeight: '600',
                        marginBottom: '4px'
                      }}>
                        {ticket.subject}
                      </div>
                      <div style={{ 
                        color: '#8A7355', 
                        fontSize: '12px',
                        fontFamily: 'monospace'
                      }}>
                        {ticket.id}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      borderRadius: '20px',
                      background: statusConfig.bgColor,
                      color: statusConfig.color,
                      fontSize: '11px',
                      fontWeight: '600'
                    }}>
                      <StatusIcon size={12} />
                      {statusConfig.label}
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '16px',
                    color: '#A57C33',
                    fontSize: '13px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <User size={14} />
                      {ticket.name}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Mail size={14} />
                      {ticket.email}
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '6px',
                    marginTop: '8px',
                    color: '#6B5B4D',
                    fontSize: '12px'
                  }}>
                    <Calendar size={12} />
                    {formatDate(ticket.created_at)}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Ticket Detail Modal */}
      {selectedTicket && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.8)',
          zIndex: 100,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center'
        }}>
          <div 
            data-testid="ticket-detail-modal"
            style={{
              background: '#1A0F0A',
              borderRadius: '20px 20px 0 0',
              width: '100%',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto',
              border: '1px solid rgba(165, 124, 51, 0.3)',
              borderBottom: 'none'
            }}
          >
            {/* Modal Header */}
            <div style={{
              position: 'sticky',
              top: 0,
              background: '#1A0F0A',
              padding: '20px',
              borderBottom: '1px solid rgba(165, 124, 51, 0.2)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ 
                  color: '#E6D7C3', 
                  fontSize: '18px', 
                  fontWeight: '600',
                  fontFamily: 'Cinzel, serif'
                }}>
                  Ticket Details
                </div>
                <div style={{ 
                  color: '#8A7355', 
                  fontSize: '12px', 
                  fontFamily: 'monospace',
                  marginTop: '4px'
                }}>
                  {selectedTicket.id}
                </div>
              </div>
              <button
                onClick={() => setSelectedTicket(null)}
                data-testid="close-modal-button"
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#8A7355',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              {/* Status Update */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  color: '#A57C33', 
                  fontSize: '12px', 
                  marginBottom: '8px',
                  textTransform: 'uppercase'
                }}>
                  Status
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => handleStatusUpdate(selectedTicket.id, key)}
                      disabled={updating}
                      data-testid={`status-${key}`}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '20px',
                        border: 'none',
                        background: selectedTicket.status === key ? config.bgColor : 'rgba(255,255,255,0.05)',
                        color: selectedTicket.status === key ? config.color : '#8A7355',
                        fontSize: '13px',
                        cursor: 'pointer',
                        opacity: updating ? 0.5 : 1
                      }}
                    >
                      {config.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Customer Info */}
              <div style={{
                background: 'rgba(215, 181, 109, 0.05)',
                borderRadius: '12px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  marginBottom: '12px'
                }}>
                  <div>
                    <div style={{ color: '#8A7355', fontSize: '11px', textTransform: 'uppercase' }}>From</div>
                    <div style={{ color: '#E6D7C3', fontSize: '15px', fontWeight: '600' }}>
                      {selectedTicket.name}
                    </div>
                    <div style={{ color: '#A57C33', fontSize: '13px' }}>
                      {selectedTicket.email}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#8A7355', fontSize: '11px', textTransform: 'uppercase' }}>Submitted</div>
                    <div style={{ color: '#A57C33', fontSize: '13px' }}>
                      {formatDate(selectedTicket.created_at)}
                    </div>
                  </div>
                </div>
                
                <div>
                  <div style={{ color: '#8A7355', fontSize: '11px', textTransform: 'uppercase', marginBottom: '6px' }}>Subject</div>
                  <div style={{ color: '#E6D7C3', fontSize: '15px', fontWeight: '600' }}>
                    {selectedTicket.subject}
                  </div>
                </div>
              </div>

              {/* Message */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  color: '#A57C33', 
                  fontSize: '12px', 
                  marginBottom: '8px',
                  textTransform: 'uppercase'
                }}>
                  Message
                </div>
                <div style={{
                  background: 'rgba(26, 15, 10, 0.8)',
                  border: '1px solid rgba(165, 124, 51, 0.2)',
                  borderRadius: '12px',
                  padding: '16px',
                  color: '#E6D7C3',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap'
                }}>
                  {selectedTicket.message}
                </div>
              </div>

              {/* Previous Response (if any) */}
              {selectedTicket.admin_response && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ 
                    color: '#10B981', 
                    fontSize: '12px', 
                    marginBottom: '8px',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <CheckCircle size={14} />
                    Previous Response
                  </div>
                  <div style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    borderRadius: '12px',
                    padding: '16px',
                    color: '#E6D7C3',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {selectedTicket.admin_response}
                    {selectedTicket.responded_at && (
                      <div style={{ 
                        color: '#6B5B4D', 
                        fontSize: '11px', 
                        marginTop: '8px' 
                      }}>
                        Sent: {formatDate(selectedTicket.responded_at)}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Response Form */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  color: '#A57C33', 
                  fontSize: '12px', 
                  marginBottom: '8px',
                  textTransform: 'uppercase'
                }}>
                  {selectedTicket.admin_response ? 'Send Another Response' : 'Send Response'}
                </div>
                <textarea
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Type your response to the customer..."
                  data-testid="response-textarea"
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '14px',
                    background: 'rgba(26, 15, 10, 0.8)',
                    border: '1px solid rgba(165, 124, 51, 0.3)',
                    borderRadius: '12px',
                    color: '#E6D7C3',
                    fontSize: '14px',
                    resize: 'vertical',
                    outline: 'none',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Admin Notes */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  color: '#8A7355', 
                  fontSize: '12px', 
                  marginBottom: '8px',
                  textTransform: 'uppercase'
                }}>
                  Internal Notes (not sent to customer)
                </div>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add internal notes for the team..."
                  data-testid="notes-textarea"
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '14px',
                    background: 'rgba(26, 15, 10, 0.8)',
                    border: '1px dashed rgba(165, 124, 51, 0.2)',
                    borderRadius: '12px',
                    color: '#A57C33',
                    fontSize: '13px',
                    resize: 'vertical',
                    outline: 'none',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Email Toggle */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '24px'
              }}>
                <button
                  onClick={() => setSendEmail(!sendEmail)}
                  data-testid="email-toggle"
                  style={{
                    width: '48px',
                    height: '28px',
                    borderRadius: '14px',
                    border: 'none',
                    background: sendEmail ? '#D7B56D' : 'rgba(107, 91, 77, 0.5)',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'background 0.2s ease'
                  }}
                >
                  <div style={{
                    width: '22px',
                    height: '22px',
                    borderRadius: '50%',
                    background: '#FFF',
                    position: 'absolute',
                    top: '3px',
                    left: sendEmail ? '23px' : '3px',
                    transition: 'left 0.2s ease'
                  }} />
                </button>
                <div>
                  <div style={{ color: '#E6D7C3', fontSize: '14px' }}>
                    Send email notification
                  </div>
                  <div style={{ color: '#8A7355', fontSize: '12px' }}>
                    Customer will receive your response via email
                  </div>
                </div>
              </div>

              {/* Send Button */}
              <button
                onClick={handleSendResponse}
                disabled={!responseText.trim() || updating}
                data-testid="send-response-button"
                style={{
                  width: '100%',
                  padding: '16px',
                  background: responseText.trim() 
                    ? 'linear-gradient(135deg, #D7B56D, #A57C33)' 
                    : 'rgba(107, 91, 77, 0.3)',
                  border: 'none',
                  borderRadius: '12px',
                  color: responseText.trim() ? '#1A0F0A' : '#6B5B4D',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: responseText.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: updating ? 0.7 : 1
                }}
              >
                <Send size={18} />
                {updating ? 'Sending...' : 'Send Response'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTicketDashboard;
